import MenuOpenOutlinedIcon from "@mui/icons-material/MenuOpenOutlined";
import { Tooltip } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import classNames from "classnames";
import { SmallBody } from "helpers/TypographyComponents";
import type { ReactNode } from "react";
import { useState } from "react";

import IosSwitch from "../IosSwitch";
import StyledButton from "../StyledButton";
import StyledPopover from "../StyledPopover";
import styles from "./style.module.scss";

export type TButtonMenuOption = {
    id: number | string;
    label: string;
    value?: string | number;
    disabled?: boolean;
};

interface IButtonOptionsMenu {
    /**
     * Btn label
     */
    label?: string | ReactNode;
    /**
     * Choose option action
     */
    handleChooseOption: (option: TButtonMenuOption) => void;
    /**
     * Change icon
     */
    icon?: ReactNode;
    /**
     * Options list
     */
    options: TButtonMenuOption[];
    /**
     * Chosen options
     */
    chosenOptions: TButtonMenuOption[];
    /**
     * Add header for menu
     */
    menuHeader?: string | ReactNode;
    /**
     * Add a footer to menu
     */
    menuFooter?: string | ReactNode;
    /**
     * Show tooltip for disabled options
     */
    disabledTooltip?: string;
    /**
     * Type of the button
     */
    optBtnType?: "switch" | "checkbox";
    /**
     * size of the button
     * small is default btn size
     * tiny is the same size as our status chip
     */
    size?: "small" | "tiny";
    /**
     * Custom styles
     */
    style?: React.CSSProperties;
    /**
     * Handle close popover action
     */
    handleClosePopoverAction?: () => void;
}
/**
 * Display button with popover menu to select multiple options
 */
const ButtonOptionsMenu = ({
    handleChooseOption,
    label,
    icon,
    options,
    chosenOptions,
    menuHeader,
    menuFooter,
    disabledTooltip,
    optBtnType = "checkbox",
    size = "small",
    style,
    handleClosePopoverAction,
}: IButtonOptionsMenu) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    const showPopover = Boolean(anchorEl);

    const closePopover = () => {
        setAnchorEl(null);
        handleClosePopoverAction?.();
    };

    const buttonSx = {
        small: { padding: "6px !important", height: "1.75rem !important", minWidth: "1.8rem" },
        tiny: { padding: "4px !important", height: "1.4rem !important", minWidth: "1.4rem" },
    };

    return (
        <div className={styles["button-options-menu"]} style={style}>
            <StyledButton buttonType="secondary" sx={buttonSx[size]} onClick={(e: any) => setAnchorEl(e.currentTarget)}>
                {icon ? icon : <MenuOpenOutlinedIcon sx={{ height: "18px", width: "18px" }} />}
                {label && <SmallBody>{label}</SmallBody>}
            </StyledButton>
            <StyledPopover open={showPopover} onClose={closePopover} anchorEl={anchorEl}>
                <div className={styles["options-wrapper"]}>
                    {menuHeader && menuHeader}
                    {options.map((opt) => {
                        const { disabled = false } = opt;
                        const checked = disabled || chosenOptions?.some(({ id }) => id === opt.id);

                        return (
                            <Tooltip
                                title={disabled && disabledTooltip}
                                key={opt.id}
                                PopperProps={{
                                    style: { zIndex: 9999 },
                                }}
                            >
                                <div
                                    onClick={() => !disabled && handleChooseOption(opt)}
                                    className={classNames({
                                        [styles["option"]]: true,
                                        [styles["disabled"]]: disabled,
                                        [styles["checkbox-option"]]: optBtnType === "checkbox",
                                        [styles["switch-option"]]: optBtnType === "switch",
                                    })}
                                >
                                    {optBtnType === "checkbox" && (
                                        <Checkbox
                                            checked={checked}
                                            className={styles["checkbox"]}
                                            disabled={disabled}
                                            size="small"
                                        />
                                    )}
                                    {optBtnType === "switch" && (
                                        <IosSwitch
                                            size="small"
                                            checked={checked}
                                            className={styles["switch"]}
                                            disabled={disabled}
                                        />
                                    )}
                                    <span className={styles["label"]}>{opt.label}</span>
                                </div>
                            </Tooltip>
                        );
                    })}
                    {menuFooter && menuFooter}
                </div>
            </StyledPopover>
        </div>
    );
};
export default ButtonOptionsMenu;
